/**
 * Tooltip — app-styled, drop-in replacement for the browser's native `title`.
 *
 * Built on Radix so it portals out of overflow-clipped containers (cards,
 * scroll areas) and handles positioning/collision automatically. Styled to
 * match the collaboration indicator popover (popover surface + border + shadow)
 * rather than the default dark chip.
 *
 * Usage — wrap any trigger:
 *   <Tooltip content="Explain this">
 *     <Button>…</Button>
 *   </Tooltip>
 *
 * The trigger is wrapped in an inline span so tooltips still show on disabled
 * buttons (a disabled control receives no pointer events itself).
 *
 * The primitives (TooltipProvider/TooltipRoot/TooltipTrigger/TooltipContent)
 * are exported too for cases that need finer control.
 */

import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { cn } from "../../lib/utils"

const TooltipProvider = TooltipPrimitive.Provider
const TooltipRoot = TooltipPrimitive.Root
const TooltipTrigger = TooltipPrimitive.Trigger

const TooltipContent = React.forwardRef(
  ({ className, sideOffset = 6, ...props }, ref) => (
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-[70] max-w-xs rounded-md border bg-popover px-2.5 py-1.5 text-xs text-popover-foreground shadow-md",
        "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        "data-[side=bottom]:slide-in-from-top-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1",
        className
      )}
      {...props}
    >
      {props.children}
      <TooltipPrimitive.Arrow className="fill-popover" width={11} height={5} />
    </TooltipPrimitive.Content>
  )
)
TooltipContent.displayName = TooltipPrimitive.Content.displayName

/**
 * Convenience wrapper — self-contained (bundles its own provider) so it works
 * anywhere without a root <TooltipProvider>. Renders children unchanged when
 * there is no content.
 */
const Tooltip = ({
  content,
  children,
  side = "top",
  align = "center",
  delayDuration = 150,
  sideOffset,
  className,
}) => {
  if (!content) return children;
  return (
    <TooltipProvider delayDuration={delayDuration}>
      <TooltipRoot>
        <TooltipTrigger asChild>
          <span className="inline-flex">{children}</span>
        </TooltipTrigger>
        <TooltipContent side={side} align={align} sideOffset={sideOffset} className={className}>
          {content}
        </TooltipContent>
      </TooltipRoot>
    </TooltipProvider>
  );
};

export { Tooltip, TooltipProvider, TooltipRoot, TooltipTrigger, TooltipContent }
export default Tooltip
